import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { st } from "../../styles.js";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Glyph, Avatar, Modal } from "../../components/ui";
import { Button, SectionHeader } from "../../components/ui/ds";
import { fmtDuePretty } from "../../lib/date-sk";
import { WEEKDAYS_SK, joinedLabel } from "../../lib/format";
import { iconChipStyle } from "../../lib/icons-resolve";
import { LIFE_CAT } from "../../lib/constants";
import { Stat } from "../buzer/TaskDetail";
import { ChallengeChatButton } from "../social/SocialFeature";

// v24 — detail výzvy. #2e — restyled to match the habit-detail sheet (TaskDetail): icon+title
// header, Stat cards, sticky footer with a filled primary CTA + secondary decline.
// v33 — postavené na zdieľanom Modal-e s reusable `footer` prop (sticky pätička); životná
// kategória sa ukáže ako dlaždica; „Špecifikácia" nadpis nad popisom zrušený.
// Reused as the PRE-JOIN preview for challenge invites (Dnes tab), not just the Výzvy screen.
// onDecline (optional) — only passed from invite cards; shows the "Odmietnuť" secondary button.
export function ChallengeInfoModal({ cid, onClose, onJoin, onDecline, joinBusy, invitedBy, me }) {
  const [d, setD] = useState(null);
  useEffect(() => { let on = true; api("/api/challenges/" + cid + "/summary").then((r) => { if (on) setD(r); }).catch(() => { if (on) setD({ err: true }); }); return () => { on = false; }; }, [cid]);
  const RANK_COL = ["#C9A227", "#9AA0A6", "#B07A4B"];  // zlato / striebro / bronz
  const ok = d && !d.err;
  const lifeCat = ok && d.life_cat ? LIFE_CAT[d.life_cat] : null;  // v33 — životná kategória
  const range = (() => {
    if (!ok || !d.start || !d.end) return null;  // bez konca (priebežná výzva) → riadok sa nezobrazí
    return fmtDuePretty(d.start) + " – " + fmtDuePretty(d.end);
  })();
  // frequency label — same convention as the challenge detail's FREKV. stat; competitions are
  // one-shot (evaluated on a date), so cadence only makes sense for daily/weekly challenges.
  const freqLabel = (!ok || d.is_competition) ? null
    : d.freq_per_week ? `${d.freq_per_week}× týžd.`
      : d.freq_days ? String(d.freq_days).split(",").map((n) => WEEKDAYS_SK[Number(n) - 1]).filter(Boolean).join("·")
        : "Denne";

  const footer = !ok ? null : d.joined ? (
    <Button variant="ghost" block onClick={onClose} style={{ color: "var(--brand-primary)" }}>
      <Icon name="check" size={18} color="currentColor" stroke={2.6} />{joinedLabel({ si: true })}
    </Button>
  ) : (
    <div style={{ display: "flex", flexDirection: "column", gap: 9, width: "100%" }}>
      <Button variant="primary" block onClick={() => onJoin(d.id)} disabled={joinBusy}>
        <Icon name="check" size={19} color="currentColor" stroke={2.6} />{joinBusy ? "…" : "Zapojiť sa"}
      </Button>
      {onDecline && (
        <Button variant="ghost" block onClick={() => onDecline(d.id)} disabled={joinBusy}>Odmietnuť</Button>
      )}
    </div>
  );

  return (
    <Modal center
      iconNode={ok ? <Glyph icon={d.icon} size={20} fallback="challenge" /> : <BuzerIcon name="challenge" size={20} color="var(--text)" />}
      iconStyle={ok ? iconChipStyle(d.icon_color) : undefined}
      title={ok ? d.name : "Výzva"}
      subtitle={ok ? <span style={{ fontFamily: "var(--ff-ui)" }}>{d.is_competition ? "Súťaž" : "Výzva"}</span> : undefined}
      /* skupinový chat výzvy — len pre zapojených účastníkov (vedľa ×) */
      headerEnd={ok && d.joined ? <ChallengeChatButton cid={d.id} name={d.name} me={me} /> : undefined}
      onClose={onClose} footer={footer}>
      {!d ? <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><div className="spin" /></div>
        : d.err ? <div style={st.err}>Detail sa nepodarilo načítať.</div>
          : (<>
            {invitedBy && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "9px 11px", background: "var(--ember-soft)", border: "1px solid var(--ember)", borderRadius: "var(--radius-md)" }}>
                <Icon name="addfriend" size={16} color="var(--ember)" />
                <span style={{ fontSize: 13, color: "var(--neutral-text-main)" }}><b>{invitedBy}</b> ťa pozýva do tejto výzvy</span>
              </div>
            )}
            {/* v33 — popis bez nadpisu „ŠPECIFIKÁCIA" (rovný rámik ako pri návyku/výzve) */}
            {d.descr && (
              <div style={{ marginBottom: 12, padding: "10px 12px", background: "var(--neutral-surface-2)", border: "1px solid var(--neutral-border)", borderRadius: "var(--radius-md)", fontSize: 13.5, color: "var(--neutral-text-main)", lineHeight: 1.45 }}>{d.descr}</div>
            )}
            {/* v33 — všetky dlaždice v JEDNOM balenom riadku (zapojení · body · frekv. · kategória · zostáva) */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Stat icon={<Icon name="friends" size={17} color="currentColor" />} value={d.participants} label="ZAPOJENÍ" />
              <Stat icon={<span style={{ fontFamily: "var(--ff-mono)", fontWeight: 700, fontSize: 17, lineHeight: 1, color: "var(--brand-primary)" }}>+</span>} value={d.points} valueColor="var(--brand-primary)" label="BODY/DEŇ" />
              {freqLabel && <Stat icon={<BuzerIcon name="calendar" size={17} color="currentColor" />} value={freqLabel} label="FREKV." />}
              {lifeCat && <Stat icon={<BuzerIcon name={lifeCat.icon} size={17} color="currentColor" />} value={lifeCat.label} label="KATEGÓRIA" />}
              {d.days_left != null && d.days_left >= 0 && <Stat icon={<BuzerIcon name="hourglass" size={17} color="currentColor" />} value={`${d.days_left} dní`} label="ZOSTÁVA" />}
            </div>

            {range && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--neutral-text-muted)" }}>
                <BuzerIcon name="calendar" size={14} color="currentColor" /><span>{range}</span>
              </div>
            )}

            {/* Rebríček „Prvé 3 miesta" má zmysel len pri súťaži; denné výzvy ho neukazujú. */}
            {d.is_competition && (<>
              <SectionHeader label="Prvé 3 miesta" style={{ margin: "18px 2px 8px" }} />
              {d.top3.length === 0 ? <div style={st.taskDesc}>Zatiaľ bez výsledkov — buď prvý/á!</div>
                : <div style={st.taskList}>{d.top3.map((m) => (
                    <div key={m.rank} className="task" style={{ ...st.task, ...st.remCard }}>
                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 13, color: "#fff", background: RANK_COL[m.rank - 1] || "var(--muted)" }}>{m.rank}</span>
                      <Avatar user={m} size={30} />
                      <span style={{ flex: 1, minWidth: 0 }}><span style={st.taskLabel}>{m.name}</span></span>
                      <span style={{ ...st.taskDesc, fontFamily: "var(--font-mono)", flexShrink: 0 }}>{m.label}</span>
                    </div>
                  ))}</div>}
            </>)}

            <SectionHeader label="Tvoji priatelia" style={{ margin: "18px 2px 8px" }} />
            {d.friends.length === 0 ? <div style={st.taskDesc}>Zatiaľ tu nie je nikto z tvojich priateľov.</div>
              : <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{d.friends.map((f, i) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--surface-solid)", border: "1px solid var(--border)", borderRadius: 99, padding: "4px 10px 4px 4px" }}>
                    <Avatar user={f} size={24} /><span style={{ fontSize: 13, fontWeight: 600 }}>{f.name}</span>
                  </span>
                ))}</div>}
          </>)}
    </Modal>
  );
}
